// src/admin/components/ContactConfig.jsx
import React, { useEffect, useState } from "react";
import { ref, get, set } from "firebase/database";
import { db } from "../../lib/firebase";
import "./ContactConfig.css";

import { CONFIG } from "../../config";
/** ---- Config ---- */
const UPLOAD_URL = CONFIG.UPLOAD_URL;
const IMAGES_BASE = CONFIG.IMAGES_BASE;
const DEFAULT_WEB3FORMS_ACCESS_KEY = String(CONFIG.WEB3FORMS_ACCESS_KEY || "").trim();
const DEFAULT_FORM_NOTIFICATION_EMAIL = String(
  CONFIG.FORM_NOTIFICATION_EMAIL || ""
).trim();

// Default rows
const blankPhone = () => ({
  number: "",
  showOnFooter: true,
  floatingIcon: false,
  icon: "",
});

const blankEmail = () => ({
  value: "",
  showOnFooter: true,
  floatingIcon: false,
  icon: "",
});

const blankSocial = () => ({
  platform: "",
  icon: "",
  link: "",
  showOnFooter: true,
  floatingIcon: false,
});

export default function ContactConfig() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [phones, setPhones] = useState([blankPhone()]);
  const [emails, setEmails] = useState([blankEmail()]);
  const [social, setSocial] = useState([blankSocial()]);

  const [bannerDesktop, setBannerDesktop] = useState("");
  const [bannerMobile, setBannerMobile] = useState("");
  const [bannerUploading, setBannerUploading] = useState("");

  const [address, setAddress] = useState("");
  const [mapEmbedUrl, setMapUrl] = useState("");

  // Which form to show on contact page
  const [formOptions, setFormOptions] = useState([]); // [{id, name}]
  const [selectedFormId, setSelectedFormId] = useState(""); // "" = no form
  const [contactFormAccessKey, setContactFormAccessKey] = useState(
    DEFAULT_WEB3FORMS_ACCESS_KEY
  );
  const [submissionEmail, setSubmissionEmail] = useState(
    DEFAULT_FORM_NOTIFICATION_EMAIL
  );

  // Meta data (SEO)
  const [metaTitle, setMetaTitle] = useState("");
  const [metaDescription, setMetaDescription] = useState("");
  const [metaKeywords, setMetaKeywords] = useState("");

  // NEW: Global floating icon style
  const [floatingShape, setFloatingShape] = useState("circle"); // "circle" | "rounded" | "square"
  const [floatingSize, setFloatingSize] = useState(56); // px

  // ---------------- Load ----------------
  useEffect(() => {
    (async () => {
      try {
        // 1) Load contactconfig
        const snap = await get(ref(db, "contactconfig"));
        if (snap.exists()) {
          const v = snap.val() || {};

          // Phones
          if (Array.isArray(v?.phones) && v.phones.length) {
            setPhones(v.phones);
          } else {
            setPhones([v?.phone || blankPhone()]);
          }

          // Emails
          if (Array.isArray(v?.emails) && v.emails.length) {
            setEmails(v.emails);
          } else {
            setEmails([v?.email || blankEmail()]);
          }

          // Social
          setSocial(
            Array.isArray(v?.socialmedia) && v.socialmedia.length
              ? v.socialmedia
              : [blankSocial()]
          );

          // Banners
          setBannerDesktop(v?.bannerDesktop || "");
          setBannerMobile(v?.bannerMobile || "");

          // Locate Us
          setAddress(v?.address || "");
          setMapUrl(v?.mapEmbedUrl || "");

          // Selected form (from new config)
          setSelectedFormId(v?.formConfigId || "");
          setContactFormAccessKey(
            v?.contactFormAccessKey || DEFAULT_WEB3FORMS_ACCESS_KEY
          );
          setSubmissionEmail(v?.submissionEmail || DEFAULT_FORM_NOTIFICATION_EMAIL);

          // Meta
          setMetaTitle(v?.meta?.metaTitle || "");
          setMetaDescription(v?.meta?.metaDescription || "");
          setMetaKeywords(v?.meta?.metaKeywords || "");

          // Floating icon style
          const shape = (v?.floatingShape || "circle").toLowerCase();
          setFloatingShape(
            ["circle", "rounded", "square"].includes(shape)
              ? shape
              : "circle"
          );
          const sizeNum = Number(v?.floatingSize);
          setFloatingSize(
            !Number.isNaN(sizeNum) && sizeNum > 20 ? sizeNum : 56
          );
        }

        // 2) Load list of forms from formConfigs
        const formsSnap = await get(ref(db, "formConfigs"));
        if (formsSnap.exists()) {
          const val = formsSnap.val();
          const arr = Object.entries(val).map(([id, data]) => ({
            id,
            name: data.formName || "Untitled form",
          }));
          setFormOptions(arr);
        } else {
          setFormOptions([]);
        }
      } catch (e) {
        console.error(e);
        setError("Failed to load contact configuration.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ---------------- Helpers ----------------
  const onPhoneChange = (i, f, v) =>
    setPhones((l) => l.map((p, x) => (x === i ? { ...p, [f]: v } : p)));

  const onEmailChange = (i, f, v) =>
    setEmails((l) => l.map((e, x) => (x === i ? { ...e, [f]: v } : e)));

  const updateSocial = (i, f, v) =>
    setSocial((l) => l.map((s, x) => (x === i ? { ...s, [f]: v } : s)));

  const addPhone = () => setPhones((l) => [...l, blankPhone()]);
  const addEmail = () => setEmails((l) => [...l, blankEmail()]);
  const addSocial = () => setSocial((l) => [...l, blankSocial()]);

  const removePhone = (i) =>
    setPhones((l) => (l.length > 1 ? l.filter((_, x) => x !== i) : l));

  const removeEmail = (i) =>
    setEmails((l) => (l.length > 1 ? l.filter((_, x) => x !== i) : l));

  const removeSocial = (i) =>
    setSocial((l) => (l.length > 1 ? l.filter((_, x) => x !== i) : l));

  // File upload/delete for icons
  const uploadIconToRow = async (file, onDone) => {
    if (!file) return;
    try {
      const f = new FormData();
      f.append("file", file);
      const res = await fetch(UPLOAD_URL, { method: "POST", body: f });
      const data = await res.json();
      if (data?.filename) onDone(data.filename);
    } catch (e) {
      console.error(e);
      setError("Icon upload failed");
    }
  };

  const deleteIconFromRow = async (filename, onDone) => {
    if (!filename) return onDone("");
    try {
      await fetch(`${IMAGES_BASE}/${encodeURIComponent(filename)}`, {
        method: "DELETE",
      });
      onDone("");
    } catch (e) {
      console.error(e);
      setError("Icon delete failed");
    }
  };

  const uploadBanner = async (which, file) => {
    if (!file) return;
    try {
      setBannerUploading(which);
      const f = new FormData();
      f.append("file", file);
      const res = await fetch(UPLOAD_URL, { method: "POST", body: f });
      const data = await res.json();
      if (data?.filename) {
        if (which === "desktop") setBannerDesktop(data.filename);
        else setBannerMobile(data.filename);
      }
    } catch (e) {
      console.error(e);
      setError("Banner upload failed");
    } finally {
      setBannerUploading("");
    }
  };

  const removeBanner = async (which) => {
    try {
      const name = which === "desktop" ? bannerDesktop : bannerMobile;
      if (name)
        await fetch(`${IMAGES_BASE}/${encodeURIComponent(name)}`, {
          method: "DELETE",
        });
      if (which === "desktop") setBannerDesktop("");
      else setBannerMobile("");
    } catch (e) {
      console.error(e);
      setError("Remove banner failed");
    }
  };

  // ---------------- Save ----------------
  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const primaryPhone = phones[0] || blankPhone();
      const primaryEmail = emails[0] || blankEmail();

      const payload = {
        // legacy single objects
        phone: primaryPhone,
        email: primaryEmail,

        // arrays
        phones,
        emails,
        socialmedia: social,

        // banners
        bannerDesktop,
        bannerMobile,

        // locate us
        address,
        mapEmbedUrl,

        // which form to render on contact page
        formConfigId: selectedFormId || "",
        contactFormAccessKey: String(contactFormAccessKey || "").trim(),
        submissionEmail: String(submissionEmail || "").trim(),

        // floating icon global style
        floatingShape, // "circle" | "rounded" | "square"
        floatingSize, // px

        // meta
        meta: {
          metaTitle,
          metaDescription,
          metaKeywords,
        },
      };

      await set(ref(db, "contactconfig"), payload);
    } catch (e) {
      console.error(e);
      setError(e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="cc-loading">Loading…</div>;

  // ---------------- UI ----------------
  return (
    <section className="cc-wrap">
      <h2 className="cc-title">Contact configuration</h2>

      {/* BANNERS */}
      <div className="cc-card">
        <h3 className="cc-subtitle">Contact Page Banners</h3>
        <div className="banner-grid">
          {/* Desktop */}
          <div className="banner-box desktop">
            <div className="banner-head">
              <strong>Desktop Banner</strong>
            </div>
            <div
              className={`banner-preview ${
                bannerDesktop ? "has-img" : "empty"
              }`}
              style={
                bannerDesktop
                  ? {
                      backgroundImage: `url("${IMAGES_BASE}/${encodeURIComponent(
                        bannerDesktop
                      )}")`,
                    }
                  : {}
              }
            />
            <div className="banner-actions">
              <label className="cc-btn">
                {bannerUploading === "desktop" ? "Uploading…" : "Upload"}
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) =>
                    uploadBanner("desktop", e.target.files?.[0])
                  }
                />
              </label>
              {bannerDesktop && (
                <button
                  className="cc-btn danger outline"
                  onClick={() => removeBanner("desktop")}
                >
                  Remove
                </button>
              )}
            </div>
          </div>

          {/* Mobile */}
          <div className="banner-box mobile">
            <div className="banner-head">
              <strong>Phone Banner</strong>
            </div>
            <div
              className={`banner-preview ${
                bannerMobile ? "has-img" : "empty"
              }`}
              style={
                bannerMobile
                  ? {
                      backgroundImage: `url("${IMAGES_BASE}/${encodeURIComponent(
                        bannerMobile
                      )}")`,
                    }
                  : {}
              }
            />
            <div className="banner-actions">
              <label className="cc-btn">
                {bannerUploading === "mobile" ? "Uploading…" : "Upload"}
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) =>
                    uploadBanner("mobile", e.target.files?.[0])
                  }
                />
              </label>
              {bannerMobile && (
                <button
                  className="cc-btn danger outline"
                  onClick={() => removeBanner("mobile")}
                >
                  Remove
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* CONTACT INFO: PHONES & EMAILS */}
      <div className="cc-card">
        <h3 className="cc-subtitle">Contact Details</h3>

        {/* Phones */}
        <h4 className="cc-section-title">Phone Numbers</h4>
        {phones.map((p, i) => (
          <div className="cc-row cc-row-multi" key={`phone-${i}`}>
            <div className="cc-col">
              <label className="cc-label">Phone Number</label>
              <input
                type="text"
                value={p.number}
                onChange={(e) =>
                  onPhoneChange(i, "number", e.target.value)
                }
                placeholder="e.g. 8797774356"
              />
            </div>

            <div className="cc-col cc-col-flags">
              <label className="cc-label">Options</label>
              <div className="cc-checkboxes">
                <label>
                  <input
                    type="checkbox"
                    checked={!!p.showOnFooter}
                    onChange={(e) =>
                      onPhoneChange(i, "showOnFooter", e.target.checked)
                    }
                  />
                  Show in footer
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={!!p.floatingIcon}
                    onChange={(e) =>
                      onPhoneChange(i, "floatingIcon", e.target.checked)
                    }
                  />
                  Floating icon
                </label>
              </div>
            </div>

            <div className="cc-col cc-col-icon">
              <label className="cc-label">Icon</label>
              {p.icon && (
                <div className="cc-icon-preview">
                  <img
                    src={`${IMAGES_BASE}/${encodeURIComponent(p.icon)}`}
                    alt="phone icon"
                  />
                </div>
              )}
              <div className="cc-icon-actions">
                <label className="cc-btn small">
                  Upload
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(e) =>
                      uploadIconToRow(e.target.files?.[0], (fn) =>
                        onPhoneChange(i, "icon", fn)
                      )
                    }
                  />
                </label>
                {p.icon && (
                  <button
                    type="button"
                    className="cc-btn danger outline small"
                    onClick={() =>
                      deleteIconFromRow(p.icon, (fn) =>
                        onPhoneChange(i, "icon", fn)
                      )
                    }
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>

            <div className="cc-col cc-col-actions">
              <button
                type="button"
                className="cc-btn danger outline"
                onClick={() => removePhone(i)}
              >
                Remove
              </button>
            </div>
          </div>
        ))}
        <button type="button" className="cc-btn outline" onClick={addPhone}>
          + Add phone
        </button>

        {/* Emails */}
        <h4 className="cc-section-title cc-section-gap">Emails</h4>
        {emails.map((e, i) => (
          <div className="cc-row cc-row-multi" key={`email-${i}`}>
            <div className="cc-col">
              <label className="cc-label">Email</label>
              <input
                type="email"
                value={e.value}
                onChange={(ev) =>
                  onEmailChange(i, "value", ev.target.value)
                }
                placeholder="e.g. adityasp193@gmail.com"
              />
            </div>

            <div className="cc-col cc-col-flags">
              <label className="cc-label">Options</label>
              <div className="cc-checkboxes">
                <label>
                  <input
                    type="checkbox"
                    checked={!!e.showOnFooter}
                    onChange={(ev) =>
                      onEmailChange(
                        i,
                        "showOnFooter",
                        ev.target.checked
                      )
                    }
                  />
                  Show in footer
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={!!e.floatingIcon}
                    onChange={(ev) =>
                      onEmailChange(
                        i,
                        "floatingIcon",
                        ev.target.checked
                      )
                    }
                  />
                  Floating icon
                </label>
              </div>
            </div>

            <div className="cc-col cc-col-icon">
              <label className="cc-label">Icon</label>
              {e.icon && (
                <div className="cc-icon-preview">
                  <img
                    src={`${IMAGES_BASE}/${encodeURIComponent(e.icon)}`}
                    alt="email icon"
                  />
                </div>
              )}
              <div className="cc-icon-actions">
                <label className="cc-btn small">
                  Upload
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(ev) =>
                      uploadIconToRow(ev.target.files?.[0], (fn) =>
                        onEmailChange(i, "icon", fn)
                      )
                    }
                  />
                </label>
                {e.icon && (
                  <button
                    type="button"
                    className="cc-btn danger outline small"
                    onClick={() =>
                      deleteIconFromRow(e.icon, (fn) =>
                        onEmailChange(i, "icon", fn)
                      )
                    }
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>

            <div className="cc-col cc-col-actions">
              <button
                type="button"
                className="cc-btn danger outline"
                onClick={() => removeEmail(i)}
              >
                Remove
              </button>
            </div>
          </div>
        ))}
        <button type="button" className="cc-btn outline" onClick={addEmail}>
          + Add email
        </button>
      </div>

      {/* SOCIAL MEDIA */}
      <div className="cc-card">
        <h3 className="cc-subtitle">Social Media</h3>
        {social.map((s, i) => (
          <div className="cc-row cc-row-multi" key={`social-${i}`}>
            <div className="cc-col">
              <label className="cc-label">Platform</label>
              <input
                type="text"
                value={s.platform}
                onChange={(ev) =>
                  updateSocial(i, "platform", ev.target.value)
                }
                placeholder="e.g. WhatsApp, Instagram"
              />
            </div>

            <div className="cc-col">
              <label className="cc-label">Link / URL</label>
              <input
                type="text"
                value={s.link}
                onChange={(ev) =>
                  updateSocial(i, "link", ev.target.value)
                }
                placeholder="https://..."
              />
            </div>

            <div className="cc-col cc-col-flags">
              <label className="cc-label">Options</label>
              <div className="cc-checkboxes">
                <label>
                  <input
                    type="checkbox"
                    checked={!!s.showOnFooter}
                    onChange={(ev) =>
                      updateSocial(i, "showOnFooter", ev.target.checked)
                    }
                  />
                  Show in footer
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={!!s.floatingIcon}
                    onChange={(ev) =>
                      updateSocial(i, "floatingIcon", ev.target.checked)
                    }
                  />
                  Floating icon
                </label>
              </div>
            </div>

            <div className="cc-col cc-col-icon">
              <label className="cc-label">Icon</label>
              {s.icon && (
                <div className="cc-icon-preview">
                  <img
                    src={`${IMAGES_BASE}/${encodeURIComponent(s.icon)}`}
                    alt="social icon"
                  />
                </div>
              )}
              <div className="cc-icon-actions">
                <label className="cc-btn small">
                  Upload
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(ev) =>
                      uploadIconToRow(ev.target.files?.[0], (fn) =>
                        updateSocial(i, "icon", fn)
                      )
                    }
                  />
                </label>
                {s.icon && (
                  <button
                    type="button"
                    className="cc-btn danger outline small"
                    onClick={() =>
                      deleteIconFromRow(s.icon, (fn) =>
                        updateSocial(i, "icon", fn)
                      )
                    }
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>

            <div className="cc-col cc-col-actions">
              <button
                type="button"
                className="cc-btn danger outline"
                onClick={() => removeSocial(i)}
              >
                Remove
              </button>
            </div>
          </div>
        ))}
        <button type="button" className="cc-btn outline" onClick={addSocial}>
          + Add social link
        </button>
      </div>

      {/* FLOATING ICON STYLE (GLOBAL) */}
      <div className="cc-card">
        <h3 className="cc-subtitle">Floating Icon Style</h3>
        <p className="cc-help">
          These settings apply to all contacts where <strong>Floating icon</strong> is enabled.
        </p>

        {/* Controls */}
        <div className="cc-row">
          <div className="cc-col">
            <label className="cc-label">Shape</label>
            <div className="cc-radio-group">
              <label>
                <input
                  type="radio"
                  name="floating-shape"
                  value="circle"
                  checked={floatingShape === "circle"}
                  onChange={(e) => setFloatingShape(e.target.value)}
                />
                Circle
              </label>
              <label>
                <input
                  type="radio"
                  name="floating-shape"
                  value="rounded"
                  checked={floatingShape === "rounded"}
                  onChange={(e) => setFloatingShape(e.target.value)}
                />
                Rounded
              </label>
              <label>
                <input
                  type="radio"
                  name="floating-shape"
                  value="square"
                  checked={floatingShape === "square"}
                  onChange={(e) => setFloatingShape(e.target.value)}
                />
                Square
              </label>
            </div>
          </div>

          <div className="cc-col">
            <label className="cc-label">Size (px)</label>
            <input
              type="range"
              min={32}
              max={96}
              step={1}
              value={floatingSize}
              onChange={(e) => setFloatingSize(Number(e.target.value))}
            />
            <input
              type="number"
              min={24}
              max={120}
              value={floatingSize}
              onChange={(e) =>
                setFloatingSize(Number(e.target.value || 0))
              }
            />
            <p className="cc-help small">
              This controls the height & width of the floating button.
            </p>
          </div>
        </div>

        {/* Preview */}
        <div className="cc-row" style={{ marginTop: 12 }}>
          <div className="cc-col">
            <label className="cc-label">Preview</label>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "8px 0",
              }}
            >
              <div
                style={{
                  width: `${floatingSize}px`,
                  height: `${floatingSize}px`,
                  borderRadius:
                    floatingShape === "circle"
                      ? "50%"
                      : floatingShape === "rounded"
                      ? "16px"
                      : "0px",
                  background: "#25D366",
                  boxShadow: "0 6px 14px rgba(0,0,0,0.25)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  fontSize: `${Math.max(
                    12,
                    Math.min(28, floatingSize / 2.2)
                  )}px`,
                }}
              >
                {/* simple icon preview (phone) */}
                <span>☎</span>
              </div>
              <span className="cc-help small">
                This is how your floating icon bubble will look on the site.
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* LOCATE US */}
      <div className="cc-card">
        <h3 className="cc-subtitle">Locate Us</h3>
        <div className="cc-row column">
          <div className="cc-col">
            <label className="cc-label">Address</label>
            <textarea
              rows={3}
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Showroom / office address shown on contact page"
            />
          </div>
          <div className="cc-col">
            <label className="cc-label">Map Embed URL</label>
            <input
              type="text"
              value={mapEmbedUrl}
              onChange={(e) => setMapUrl(e.target.value)}
              placeholder="Google Maps embed URL or map link"
            />
          </div>
        </div>
      </div>

      {/* CONTACT PAGE FORM SELECTION */}
      <div className="cc-card">
        <h3 className="cc-subtitle">Contact Page Form</h3>
        <p className="cc-help">
          Choose which form (from <strong>Form Config</strong>) should appear
          on the contact page. Select &quot;No form&quot; to hide it.
        </p>

        <div className="cc-row">
          <div className="cc-col">
            <label className="cc-label">Select Form</label>
            <select
              value={selectedFormId}
              onChange={(e) => setSelectedFormId(e.target.value)}
            >
              <option value="">No form</option>
              {formOptions.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </div>
          <div className="cc-col">
            <label className="cc-label">Fallback Web3Forms Access Key</label>
            <input
              type="text"
              value={contactFormAccessKey}
              onChange={(e) => setContactFormAccessKey(e.target.value)}
              placeholder="Used when no dynamic form is selected"
            />
          </div>
        </div>

        <div className="cc-row">
          <div className="cc-col">
            <label className="cc-label">Form submission email</label>
            <input
              type="email"
              value={submissionEmail}
              onChange={(e) => setSubmissionEmail(e.target.value)}
              placeholder="interiorregency6@gmail.com"
            />
            <p className="cc-help small">
              If set, form notifications are routed to this email.
            </p>
          </div>
        </div>

        {formOptions.length === 0 && (
          <p className="cc-help small">
            No forms found yet. Create forms in the <strong>Form Config</strong> tab.
          </p>
        )}
      </div>

      {/* META DATA */}
      <div className="cc-card">
        <h3 className="cc-subtitle">Page Meta Data (SEO)</h3>
        <div className="cc-row column">
          <div className="cc-col">
            <label className="cc-label">Meta Title</label>
            <input
              type="text"
              value={metaTitle}
              onChange={(e) => setMetaTitle(e.target.value)}
              placeholder="Contact Regency Interiors | Interior Design Experts"
            />
          </div>
          <div className="cc-col">
            <label className="cc-label">Meta Description</label>
            <textarea
              rows={3}
              value={metaDescription}
              onChange={(e) => setMetaDescription(e.target.value)}
              placeholder="Short SEO description (under 160 characters)"
            />
          </div>
          <div className="cc-col">
            <label className="cc-label">Meta Keywords</label>
            <input
              type="text"
              value={metaKeywords}
              onChange={(e) => setMetaKeywords(e.target.value)}
              placeholder="Regency Interiors, contact, interior design, Bangalore"
            />
          </div>
        </div>
      </div>

      {error && <p className="cc-error">{error}</p>}

      <div className="cc-actions">
        <button className="cc-btn primary" onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save contactconfig"}
        </button>
      </div>
    </section>
  );
}
